import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DownloadCloud, CheckCircle2, RefreshCcw, WifiOff, Clock, HardDrive } from "lucide-react";
import api from "../../lib/api";

/* Spec §15 — per-trip Offline Travel Pack ceremony.
   Renders inside the trip detail page. Shows sync status, downloads the pack,
   and stores it in localStorage under `offline_pack_<tripId>` for offline reads. */

const LS_KEY = (tid) => `offline_pack_${tid}`;
const DEVICE_ID_KEY = "offline_device_id";

function getDeviceId() {
  let d = localStorage.getItem(DEVICE_ID_KEY);
  if (!d) {
    d = `dev-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
    localStorage.setItem(DEVICE_ID_KEY, d);
  }
  return d;
}

function getDeviceLabel() {
  const ua = navigator.userAgent || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "This device";
}

export default function OfflineReadyCard({ tripId, tripTitle }) {
  const [pack, setPack] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  const cached = () => {
    try {
      const raw = localStorage.getItem(LS_KEY(tripId));
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };

  const knownRevision = cached()?.revision || 0;

  const check = async () => {
    try {
      const { data } = await api.post(`/trips/${tripId}/offline-pack/status`, {
        known_revision: knownRevision,
        device_id: getDeviceId(),
        device_label: getDeviceLabel(),
      });
      setStatus(data);
      setLastCheckedAt(new Date());
    } catch (e) {
      // gracefully degrade — still let user try to download
    }
  };

  useEffect(() => {
    setPack(cached());
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/trips/${tripId}/offline-pack`);
      localStorage.setItem(LS_KEY(tripId), JSON.stringify(data));
      setPack(data);
      toast.success("Offline pack ready — you can open this trip without a network.");
      await check();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't download the offline pack");
    } finally { setBusy(false); }
  };

  const removeLocal = () => {
    localStorage.removeItem(LS_KEY(tripId));
    setPack(null);
    toast.success("Removed from this device");
    check();
  };

  const readyStateLabel = () => {
    if (!pack) return { label: "Not prepared", tone: "amber", Icon: WifiOff };
    if (status?.stale) return { label: "Update available", tone: "amber", Icon: RefreshCcw };
    return { label: "Offline Ready", tone: "green", Icon: CheckCircle2 };
  };
  const st = readyStateLabel();

  const sizeKB = pack ? Math.round(new Blob([JSON.stringify(pack)]).size / 1024) : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="offline-ready-card">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl border grid place-items-center shrink-0 ${
          st.tone === "green" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
          "bg-amber-50 text-amber-700 border-amber-200"
        }`}>
          <st.Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500">Spec §15 · Offline Travel Pack</div>
          <div className="font-serif font-800 text-lg text-slate-900 mt-0.5">{st.label}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {pack
              ? <>Trip revision <b>{pack.revision}</b> · {sizeKB} KB on device{status?.server_revision != null && <> · server has revision <b>{status.server_revision}</b></>}</>
              : "Download this trip to open it without a network. Sensitive fields stay in the vault."}
          </div>
          {lastCheckedAt && (
            <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Last checked {lastCheckedAt.toLocaleTimeString()}</div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={download} disabled={busy}
          data-testid="offline-download"
          className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:brightness-110 text-white text-sm font-700 px-4 py-2 disabled:opacity-60">
          {busy ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
          {pack ? (status?.stale ? "Update pack" : "Refresh pack") : "Download offline pack"}
        </button>
        {pack && (
          <button onClick={removeLocal}
            data-testid="offline-remove"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-700 px-4 py-2">
            <HardDrive className="w-4 h-4" /> Remove from this device
          </button>
        )}
      </div>

      {pack && (
        <div className="mt-4 pt-4 border-t border-slate-100 text-[11px] text-slate-500 leading-relaxed">
          {pack.sensitive_note}
        </div>
      )}
    </div>
  );
}
